import { useEffect, useState } from 'react';
import { LogIn, LogOut, Loader2, ShieldAlert, Caravan, Phone } from 'lucide-react';
import * as api from '../../lib/api';
import { useShiftStore } from '../../store/useShiftStore';
import { formatDate, formatFeet } from '../../lib/format';
import NoFacility from '../../components/attendant/NoFacility';

/**
 * Move-in / move-out. The arrivals list is the attendant's shift worklist:
 * who is expected, in which space, and whether anything will stop them at the
 * gate — most often lapsed insurance.
 */
export default function ArrivalsPage() {
  const facilityId = useShiftStore((s) => s.facilityId);
  const facilities = useShiftStore((s) => s.facilities);
  const [arrivals, setArrivals] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    // Bail out into an explicit state, never leaving `state` on 'loading'.
    if (!facilityId) { setState('no_facility'); return; }
    setState('loading');
    try {
      const { arrivals: list } = await api.listArrivals(facilityId);
      setArrivals(list);
      setState('ready');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  };

  useEffect(() => { setDone(null); load(); }, [facilityId]);

  const act = async (lease, action) => {
    setBusyId(lease.public_id);
    setError(null);
    try {
      const { result } = action === 'in'
        ? await api.moveIn(facilityId, lease.public_id)
        : await api.moveOut(facilityId, lease.public_id, null);
      setDone({ action, ...result });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  if (state === 'no_facility') return <NoFacility hasAnyFacility={facilities.length > 0} />;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
      <h1 className="text-xl font-extrabold tracking-tight text-slate-900">Arrivals</h1>
      <p className="mt-1 text-sm text-slate-600">Move-ins expected in the next seven days.</p>

      {done && (
        <p role="status" className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-900">
          {done.action === 'in'
            ? `${done.tenant} moved into unit ${done.unit_number}.`
            : `${done.tenant} moved out of unit ${done.unit_number}. Lease ${done.status}; unit set to ${done.unit_status} for a condition check.`}
        </p>
      )}

      {error && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

      {state === 'loading' && (
        <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" /></div>
      )}

      {state === 'ready' && arrivals.length === 0 && (
        <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <Caravan className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-bold text-slate-900">No arrivals expected</p>
          <p className="mt-1 text-xs text-slate-500">Nothing scheduled in the next seven days.</p>
        </div>
      )}

      <ul className="mt-4 space-y-3">
        {arrivals.map((lease) => {
          const insuranceLapsed = lease.insurance_expires_on
            && new Date(`${lease.insurance_expires_on}T00:00:00`) < new Date();
          const noInsurance = !lease.insurance_expires_on;

          return (
            <li key={lease.public_id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-slate-900">
                    {lease.first_name} {lease.last_name}
                    <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                      Unit {lease.unit_number}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-slate-600">
                    {lease.lease_number} · arriving {formatDate(lease.starts_on)}
                  </p>
                  <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1">
                      <Caravan className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
                      {lease.vehicle_label ?? 'No vehicle on file'}
                      {lease.vehicle_length_ft && ` (${formatFeet(lease.vehicle_length_ft)})`}
                    </span>
                    {lease.license_plate && <span className="font-mono">{lease.license_plate}</span>}
                    {lease.phone && (
                      <a href={`tel:${lease.phone}`} className="inline-flex items-center gap-1 hover:underline">
                        <Phone className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />{lease.phone}
                      </a>
                    )}
                  </p>

                  {(insuranceLapsed || noInsurance) && (
                    <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-100 px-2.5 py-1.5 text-[12px] font-bold text-amber-900">
                      <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
                      {noInsurance ? 'No insurance on file' : `Insurance expired ${formatDate(lease.insurance_expires_on)}`}
                      {' '}— collect proof before admitting
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 gap-2">
                  <button type="button" onClick={() => act(lease, 'in')} disabled={busyId === lease.public_id}
                          className="btn-primary py-2.5">
                    {busyId === lease.public_id
                      ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      : <LogIn className="h-4 w-4" aria-hidden="true" />}
                    Move in
                  </button>
                  <button type="button" onClick={() => act(lease, 'out')} disabled={busyId === lease.public_id}
                          className="btn-ghost py-2.5" title="Cancel this reservation and release the space">
                    <LogOut className="h-4 w-4" aria-hidden="true" /> Cancel
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
